import { parsePhoneNumber } from 'libphonenumber-js/max';
import type { Tagged } from 'type-fest';

/**
 * 人物を識別しうる識別子
 */
export type Identifier = {
  type: Identifier.Type;
  value: string;
};

export namespace Identifier {
  /**
   *  識別子種別
   *
   * ex)
   * - karteVisitorId, karteUserId,
   * - email, phone
   * - customerId, memberId
   */
  export type Type = Tagged<string, 'IdentifierType'>;
  export namespace Type {
    const Pattern = /^[a-zA-Z0-9_]+$/;
    export const FromString = (s: string): Type => {
      if (!Pattern.test(s)) {
        throw new Error(`Invalid Identifier.Type: ${JSON.stringify(s)} (allowed: [a-zA-Z0-9_]+)`);
      }
      return s as Type;
    };
  }

  export type Props = {
    type: string;
    value: string;
  };

  // 識別子種別ごとの value 正規化処理。
  // 未登録の種別はそのまま素通しする。
  const normalizers: Record<string, (value: string) => string> = {
    // 電話番号は E.164 形式（例: '+818012345678'）に正規化する。
    // 解釈できない値は元値のまま保存する（V1 の MalformedNumber と同等の挙動）。
    phone: (value: string): string => {
      try {
        const direct = parsePhoneNumber(value, 'JP');
        if (direct.isValid()) return direct.format('E.164');
      } catch {
        // fallthrough
      }
      try {
        const withPlus = parsePhoneNumber(`+${value}`, 'JP');
        if (withPlus.isValid()) return withPlus.format('E.164');
      } catch {
        // fallthrough
      }
      return value;
    },
  };

  export const Construct = (props: Props): Identifier => {
    const type = Type.FromString(props.type);
    const normalize = normalizers[type];
    return {
      type,
      value: normalize ? normalize(props.value) : props.value,
    };
  };

  export const ToString = (identifier: Identifier): string => {
    return `${identifier.type}:${identifier.value}`;
  };

  export const FromString = (s: string): Identifier => {
    // value にコロンが含まれるケース（例: "externalId:urn:uuid:123"）に対応するため、
    // split(':') ではなく最初のコロンの位置で分割する
    const colonIndex = s.indexOf(':');
    if (colonIndex === -1) throw new Error(`Invalid Identifier string: ${s}`);
    const type = s.substring(0, colonIndex);
    const value = s.substring(colonIndex + 1);
    return {
      type: Type.FromString(type),
      value,
    };
  };
}
